import { useEffect, useState } from "react";
import { Robot, UserCircle, Sparkle, TrendDown } from "@phosphor-icons/react";
import { api, inr } from "@/lib/api";

const Col = ({ icon: Icon, title, accent, data, testId }) => (
  <div data-testid={testId} className={`flex-1 rounded-lg border p-4 ${accent ? "border-[#FF6B35]/30 bg-[#FF6B35]/5" : "border-[#E5E7EB] bg-[#FAFAFB]"}`}>
    <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${accent ? "text-[#E85A25]" : "text-[#8a90a0]"}`}>
      <Icon size={13} weight="fill" /> {title}
    </div>
    <div className="mt-2 font-heading text-2xl font-extrabold tracking-tighter text-[#0A0D14]">
      {data.cpl ? `${inr(data.cpl)} CPL` : "—"}
    </div>
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#525866]">
      <span><b className="text-[#0A0D14]">{data.leads}</b> leads</span>
      <span>CTR <b className="text-[#0A0D14]">{data.ctr}%</b></span>
      <span><b className="text-[#0A0D14]">{inr(data.spend)}</b> spent</span>
      <span><b className="text-[#0A0D14]">{data.ads}</b> ads</span>
    </div>
  </div>
);

export const AIImpactReport = () => {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/insights/ai-impact").then(({ data }) => setR(data)).catch(() => {});
  }, []);

  if (!r || !r.ai?.ads) return null;
  const better = r.ai.cpl > 0 && r.human.cpl > 0 && r.ai.cpl < r.human.cpl;
  const pct = better ? Math.round((1 - r.ai.cpl / r.human.cpl) * 100) : 0;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="ai-impact-report">
      <div className="flex flex-wrap items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center">
          <Sparkle size={16} weight="duotone" className="text-[#FF6B35]" />
        </span>
        <h3 className="font-heading text-sm font-extrabold tracking-tight text-[#0A0D14]">AI Creative Team Impact</h3>
        <span className="text-[11px] text-[#8a90a0] ml-auto">Last {r.window_days} days · live Meta data</span>
      </div>

      {better && (
        <div className="mt-3 rounded-lg bg-success/5 border border-success/25 px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid="ai-impact-savings">
          <TrendDown size={15} weight="bold" className="text-[#00953e]" />
          <span className="text-xs font-bold text-[#00953e]">
            AI-published ads deliver leads {pct}% cheaper{r.savings > 0 ? ` — ~${inr(r.savings)} saved on ${r.ai.leads} leads vs your other ads' CPL` : ""}
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <Col icon={Robot} title="AI-published ads" accent data={r.ai} testId="ai-impact-ai-col" />
        <Col icon={UserCircle} title="Your other ads" data={r.human} testId="ai-impact-human-col" />
      </div>

      {r.top_ai_ads?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Top AI ads by leads</div>
          <div className="mt-2 space-y-1.5">
            {r.top_ai_ads.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-xs" data-testid={`ai-impact-top-ad-${a.id}`}>
                <span className="font-semibold text-[#0A0D14] truncate flex-1" title={a.name}>{a.name}</span>
                <span className="text-[#8a90a0] truncate max-w-[180px] hidden md:block">{a.campaign_name}</span>
                <span className="font-bold text-[#0A0D14] shrink-0">{a.leads} leads</span>
                <span className="text-[#525866] shrink-0">{a.cpl ? `${inr(a.cpl)} CPL` : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
